import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Logo from "../components/Logo";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch("/api/reset-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.detail || "Reset failed. Please try again.");
        return;
      }
      setDone(true);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-gradient-hero text-white flex flex-col">
        <div className="px-6 md:px-12 pt-7">
          <Logo size="sm" />
        </div>
        <div className="flex-1 grid place-items-center px-6 py-10">
          <div className="w-full max-w-md glass-strong rounded-3xl p-8 md:p-10 text-center">
            <div className="text-[11px] tracking-[0.28em] uppercase text-[#D4AF37] mb-3">Reset link</div>
            <h1 className="font-display text-3xl leading-tight">Invalid link.</h1>
            <p className="text-white/55 mt-3">This reset link is missing or malformed.</p>
            <Link to="/forgot-password" className="mt-6 inline-block text-sm text-[#F2DE94] hover:text-white underline">
              Request a new reset link
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-hero text-white flex flex-col">
      <div className="px-6 md:px-12 pt-7 flex items-center justify-between">
        <Logo size="sm" />
        <Link to="/login" className="text-xs text-white/50 hover:text-white">
          Back to sign in
        </Link>
      </div>

      <div className="flex-1 grid place-items-center px-6 md:px-12 py-10">
        <div className="w-full max-w-md glass-strong rounded-3xl p-8 md:p-10">
          <div className="text-[11px] tracking-[0.28em] uppercase text-[#D4AF37] mb-3">
            New password
          </div>

          {done ? (
            <>
              <h1 className="font-display text-4xl leading-tight">Password updated.</h1>
              <p className="text-white/55 mt-3">You can now sign in with your new password.</p>
              <Link
                to="/login"
                className="mt-8 inline-block w-full text-center py-3 rounded-xl bg-[#D4AF37] text-black font-semibold"
              >
                Sign in
              </Link>
            </>
          ) : (
            <>
              <h1 className="font-display text-4xl leading-tight">Choose a new password.</h1>
              <p className="text-white/55 mt-3">Must be at least 8 characters.</p>

              <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                <input
                  type="password"
                  placeholder="New password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 focus:outline-none focus:border-[rgba(212,175,55,0.5)] text-white placeholder:text-white/40"
                />
                <input
                  type="password"
                  placeholder="Confirm new password"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 focus:outline-none focus:border-[rgba(212,175,55,0.5)] text-white placeholder:text-white/40"
                />

                {error && (
                  <div
                    className="border border-red-400 text-red-300 text-sm px-4 py-3 rounded-xl"
                    style={{ backgroundColor: "rgba(239,68,68,0.08)" }}
                  >
                    {error}
                    {(error.includes("expired") || error.includes("Invalid")) && (
                      <div className="mt-2">
                        <Link to="/forgot-password" className="text-[#F2DE94] underline text-sm">
                          Request a new reset link
                        </Link>
                      </div>
                    )}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className={`w-full py-3 rounded-xl bg-[#D4AF37] text-black font-semibold transition-opacity ${submitting ? "opacity-60" : ""}`}
                >
                  {submitting ? "Updating…" : "Update password"}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
